import { client } from "../../hooks/Client";
import { useFirestoreOrderBy } from "../../firebase/useFirestore";
import { useHistory } from "react-router-dom";
import Tooltip from "../common/Tooltip";
import { useState, useEffect, useContext } from "react";
import { SavedIcon } from "../../StateManagment/SavedIcon";
import { db, timestamp } from "../../firebase/config";
import uuid from "react-uuid";
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import SelectActivities from "../effects/SelectActivities";
import IndicatorsCount from "../effects/IndicatorsCount.jsx";
import EffectDeleteContainer from "../effects/EffectDeleteContainer";
import DragIconTableData from "../common/DragIconTableData.jsx";
import EditEffectTableData from "../effects/EditEffectTableData.jsx";
import Table from "../common/table/Table.jsx";
import ReadOnlyGuard from "../common/ReadOnlyGuard";
import AddEffectFlow from "./add/AddEffectFlow.jsx";
import useParentPortfolios from "../../hooks/useParentPortfolios";

/**
 * Reusable CRUD body for Effecten. Rendered both on the dedicated Effects route
 * and inline in the TOC navigator (F7). Owns its table, the Meetstandaard effect
 * picker (sector tabs + cascade write to QuestionnaireFields/MatrixQuestionRows/
 * Columns), manual add and portfolio-subscribe. The route wrapper keeps the
 * page chrome (PageHeader, Prev/Next).
 */
const EffectManager = () => {
  // Context
  const [, setSaved] = useContext(SavedIcon);

  // State
  const [dragArray, setDragArray] = useState([]);
  const [showAddContainer, setShowAddContainer] = useState(false);

  // Hooks
  const history = useHistory();

  // Resolved once for the whole table — EditEffectTableData renders per row
  // and would otherwise open its own CompagnyMeta listeners per effect.
  const { portfolioNameById } = useParentPortfolios();

  // Firestore
  const effects = useFirestoreOrderBy("OutputEffects", "Position", "asc");

  useEffect(() => {
    setDragArray(effects);
  }, [effects]);

  // DRAG & DROP: reorder items and update Firestore
  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(dragArray);
    const [movedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, movedItem);

    setDragArray(items);

    items.forEach((dragArrayItem, idx) => {
      db.collection("OutputEffects")
        .doc(dragArrayItem.docid)
        .update({ Position: idx + 1 });
    });

    setSaved("flex");
  };

  // Update the title
  const titleHandler = (e) => {
    const effect = e.target.value;
    const docid = e.target.dataset.docid;

    db.collection("OutputEffects")
      .doc(docid)
      .update({ Effect: effect })
      .then(() => setSaved("flex"));
  };

  // Update the description
  const descriptionHandler = (e) => {
    const description = e.target.value;
    const docid = e.target.dataset.docid;

    db.collection("OutputEffects")
      .doc(docid)
      .update({ Description: description })
      .then(() => setSaved("flex"));
  };

  // Add a new effect
  const addEffect = () => {
    db.collection("OutputEffects")
      .add({
        Effect: "",
        Description: "",
        Position: effects.length + 1,
        ID: uuid(),
        Compagny: client,
        Term: "long",
        CompagnyID: client,
        Timestamp: timestamp,
        type: "manual",
      })
      .then(() => setSaved("flex"));
  };

  const tableColumns = [
    { key: "drag", label: "", searchable: false },
    { key: "effect", label: "EFFECT" },
    { key: "description", label: "BESCHRIJVING", searchable: false },
    { key: "questions", label: "VRAGEN", searchable: false },
    { key: "activities", label: "ACTIVITEIT(EN)", searchable: false },
    { key: "delete", label: "VERWIJDEREN", searchable: false },
  ];

  return (
    <>
      <ReadOnlyGuard edit="strategy.edit">
        {(canEdit) => (
      <Table
        columns={tableColumns}
        items={dragArray || []}
        getItemKey={(effect) => effect.ID}
        renderRow={(effect) => (
          <>
            <DragIconTableData effect={effect} output={null} question={null} />
            <EditEffectTableData
              effect={effect}
              titleHandler={titleHandler}
              descriptionHandler={descriptionHandler}
              portfolioNameById={portfolioNameById}
            />
            <td>
              <div id="indicator-effect-icons-container">
                <Tooltip content={"Indicatoren toevoegen"} top="-60px" width={"30px"}>
                  <AddCircleOutlineOutlinedIcon
                    className="table-delete-icon"
                    onClick={() => history.push(`/${client}/addindicators/${effect.ID}`)}
                  />
                </Tooltip>
                <Tooltip content={"Aantal indicatoren"} top="-60px" width="30px">
                  <p className="common-table__count-pill">
                    <IndicatorsCount effectId={effect.ID} />
                  </p>
                </Tooltip>
              </div>
            </td>
            <td>
              <SelectActivities effect={effect} />
            </td>
            <td>
              {canEdit && <EffectDeleteContainer effect={effect} />}
            </td>
          </>
        )}
        searchAccessor={(effect) => effect?.Effect || ""}
        searchPlaceholder="Zoeken op effect"
        emptyMessage="Geen effecten gevonden."
        draggable
        droppableId="effects"
        onDragEnd={handleDragEnd}
        addRow={canEdit ? {
          toolTipContent: "Effect toevoegen",
          onClick: () => setShowAddContainer(!showAddContainer),
        } : undefined}
      />
        )}
      </ReadOnlyGuard>

      <AddEffectFlow
        open={showAddContainer}
        setOpen={setShowAddContainer}
        onOwnAdd={addEffect}
        effectsLength={(effects || []).length}
      />
    </>
  );
};

export default EffectManager;
